import { matchesProject } from './projectHelpers'
import { debounce, fastConnection } from './utils'

/**
 * Projekte list + filter.
 *
 * The server renders the full text list (projects-list.php) as the SEO / no-JS
 * payload, with every filterable value on each row as a data-* attribute and the
 * responsive teaser <img> parked in a hidden [data-list-image]. This module reads
 * that data, builds the image grid client-side, filters both views by
 * view/tags/search, mirrors the state in the URL, and paginates only the image
 * grid (10 at a time, appended on scroll — the text list always shows every match).
 */

const TAXONOMIES = ['bauwerksart', 'leistung', 'sia-phase']
const BATCH = 10

// data-sia-phase -> dataset.siaPhase
const dataKey = (name) => name.replace(/-(\w)/g, (_, c) => c.toUpperCase())

// dataset returns decoded text, so re-injecting nr/title into innerHTML needs escaping.
const esc = (value) =>
  String(value).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c])

// Projects without a teaser image get a gray placeholder box instead.
const PLACEHOLDER = '<div class="project-teaser-link-placeholder" aria-hidden="true"></div>'

// The sticky filter (see 55_projects.sass) pins the always-visible top bar, the
// collapsible filter panel, and the column-label header row above the list. Its
// full height — measured only while list view is active AND the panel is closed,
// exactly the state the typewriter scroll-snap is armed in — feeds
// --projects-chrome-height, so a snapped row settles flush under that chrome.
//
// It also flags .is-panel-animating on <html> for the length of each panel
// open/close transition, so the snap CSS holds snapping OFF while the panel
// resizes the filter and scroll anchoring shifts the page. That's the one window
// a plain :has(:not(.is-active)) gate can't cover, because toggle.js drops
// .is-active at the START of the close animation. Returns measure() so apply()
// can re-read after the view class flips (the header row is only measurable in
// list view).
//
// EXPERIMENTAL — DISABLED (typewriter scroll-snap). This function existed only to
// feed the snap: it set --projects-chrome-height and toggled .is-panel-animating,
// both consumed exclusively by the (also disabled) snap rules in 55_projects.sass.
// To re-enable: un-comment this block, the `measureChrome` assignment in the
// [data-projects] loop, the measureChrome() call in apply(), and the snap rules.
/*
const setupFilterChrome = (container) => {
  const root = document.documentElement
  const filter = container.querySelector('[data-projects-filter]')
  const trigger = container.querySelector('[data-toggle-trigger]')
  const panel = container.querySelector('[data-toggle-auto-height]')
  if (!filter) return () => {}

  const measure = () => {
    if (!document.body.classList.contains('is-view-liste')) return
    if (container.classList.contains('is-active')) return
    root.style.setProperty('--projects-chrome-height', `${filter.offsetHeight}px`)
  }

  // transitionend on max-height is the exact end of the panel animation; the
  // timeout is a safety net for the no-op case (re-clicking with no height change
  // fires no transitionend).
  let animTimer = null
  const endAnim = () => root.classList.remove('is-panel-animating')
  trigger?.addEventListener('click', () => {
    root.classList.add('is-panel-animating')
    clearTimeout(animTimer)
    animTimer = setTimeout(endAnim, 300)
  })
  panel?.addEventListener('transitionend', (e) => {
    if (e.propertyName !== 'max-height') return
    clearTimeout(animTimer)
    endAnim()
    measure() // panel just settled -> refresh the exact closed-chrome height
  })

  measure()
  window.addEventListener('resize', measure)
  window.addEventListener('load', measure)
  return measure
}
*/

const teaser = (it) => `
  <div class="project-teaser-item${it.size ? ` project-teaser-item-${esc(it.size)}` : ''}"${it.imageRatio ? ` style="--project-image-ratio: ${it.imageRatio}"` : ''}>
    <a href="${it.href}" class="project-teaser-link"${it.prefetchImage ? ` data-prefetch-image="${esc(it.prefetchImage)}"` : ''}>
      ${it.image || PLACEHOLDER}
      <div class="project-teaser-link-bottom font-mono fs-12 lh-16 l:fs-14">
        <div class="project-teaser-link-bottom-left">${esc(it.nr)}</div>
        <div class="project-teaser-link-bottom-right">${esc(it.title)}</div>
      </div>
    </a>
  </div>`

document.querySelectorAll('[data-projects]').forEach((container) => {
  const form = container.querySelector('[data-projects-form]')
  const grid = container.querySelector('[data-projects-grid]')
  const sentinel = container.querySelector('[data-projects-sentinel]')

  // EXPERIMENTAL — DISABLED (typewriter scroll-snap): fed the chrome measurement.
  // See setupFilterChrome above and the snap rules in 55_projects.sass.
  // const measureChrome = setupFilterChrome(container)

  if (!form || !grid || !sentinel) return

  // Build the index and pull each teaser image out of its hidden carrier.
  const index = [...container.querySelectorAll('[data-list-item]')].map((el) => {
    const carrier = el.querySelector('[data-list-image]')
    const image = carrier ? carrier.innerHTML.trim() : ''
    // Natural width/height ratio of the teaser image — drives the displayed
    // image width in CSS so the bottom bar can match it exactly.
    const imgEl = carrier?.querySelector('img')
    const imgW = Number(imgEl?.getAttribute('width'))
    const imgH = Number(imgEl?.getAttribute('height'))
    const imageRatio = imgW > 0 && imgH > 0 ? Math.round((imgW / imgH) * 10000) / 10000 : 0
    carrier?.remove()

    const terms = {}
    for (const tax of TAXONOMIES) {
      terms[tax] = (el.dataset[dataKey(tax)] || '').split(',').filter(Boolean)
    }

    return {
      el,
      permalink: el.getAttribute('href'),
      href: el.getAttribute('href'),
      nr: el.dataset.nr || '',
      title: el.dataset.title || '',
      searchIndex: el.dataset.searchIndex || '',
      terms,
      image,
      imageRatio,
      size: el.dataset.teaserSize || '',
      prefetchImage: el.dataset.prefetchImage || '',
    }
  })

  let state = { view: 'bilder', search: '', bauwerksart: [], leistung: [], 'sia-phase': [] }
  let filtered = index
  let rendered = 0

  // The form is the single source of truth for the current filter.
  const readState = () => {
    const fd = new FormData(form)
    return {
      view: fd.get('anzeige') === 'liste' ? 'liste' : 'bilder',
      search: (fd.get('search') || '').trim(),
      bauwerksart: fd.getAll('bauwerksart[]'),
      leistung: fd.getAll('leistung[]'),
      'sia-phase': fd.getAll('sia-phase[]'),
    }
  }

  // Search AND (per taxonomy: OR within, AND across).
  const matches = (it) =>
    matchesProject(state.search, it.searchIndex) &&
    TAXONOMIES.every((tax) => state[tax].length === 0 || state[tax].some((slug) => it.terms[tax].includes(slug)))

  // Each item renders into a DOM node exactly once and gets re-appended on
  // every filter change. Reusing the node keeps already-loaded images in
  // their `lazyloaded` state, so lazysizes doesn't replay the blur-up
  // effect on teasers that were already visible. Only the link href is
  // refreshed — it carries the current filter query.
  const teaserNode = (it) => {
    if (!it.node) {
      const tpl = document.createElement('template')
      tpl.innerHTML = teaser(it).trim()
      it.node = tpl.content.firstElementChild
    }
    it.node.querySelector('.project-teaser-link').setAttribute('href', it.href)
    return it.node
  }

  const renderNextBatch = () => {
    const next = filtered.slice(rendered, rendered + BATCH)
    if (next.length === 0) return
    grid.append(...next.map(teaserNode))
    rendered += next.length
  }

  const resetGrid = () => {
    grid.replaceChildren()
    rendered = 0
    renderNextBatch()
  }

  const applyView = () => {
    const liste = state.view === 'liste'
    document.body.classList.toggle('is-view-liste', liste)
    document.body.classList.toggle('is-view-images', !liste)
  }

  // The active filter as a query string — mirrored into the URL and forwarded onto every
  // project link.
  const buildQuery = () => {
    const p = new URLSearchParams()
    if (state.view === 'liste') p.set('anzeige', 'liste')
    if (state.search) p.set('search', state.search)
    for (const tax of TAXONOMIES) {
      if (state[tax].length) p.set(tax, state[tax].join(','))
    }
    return p.toString()
  }

  // Carry the current filter onto each project's URL so that opening a project and clicking
  // its "schliessen" link returns to this list in the same state (projekt.php reads the
  // params back). No filter -> the plain permalink, so project URLs stay clean by default.
  const syncLinks = (qs) => {
    const suffix = qs ? `?${qs}` : ''
    for (const it of index) {
      it.href = it.permalink + suffix
      it.el.setAttribute('href', it.href)
    }
  }

  const writeUrl = (qs) => {
    history.replaceState(null, '', qs ? `?${qs}` : location.pathname)
  }

  // Each group's "Alle" box is checked exactly when no tag in that group is
  // selected (the group filters nothing). Kept in sync after every apply.
  const syncAllStates = () => {
    for (const tax of TAXONOMIES) {
      const all = form.querySelector(`[data-filter-all="${tax}"]`)
      if (all) all.checked = form.querySelectorAll(`input[name="${tax}[]"]:checked`).length === 0
    }
  }

  const apply = () => {
    state = readState()
    syncAllStates()
    filtered = index.filter(matches)

    // Nothing matched the current filter/search — a shared message replaces both
    // views (see .is-empty in 55_projects.sass), so the user isn't left staring at
    // an empty grid or a lone table header.
    container.classList.toggle('is-empty', filtered.length === 0)

    // "!" on the Filter button while a filter or search narrows the set —
    // the Bilder/Liste view deliberately doesn't count (55_projects.sass).
    container.classList.toggle(
      'is-filtered',
      state.search !== '' || TAXONOMIES.some((tax) => state[tax].length > 0)
    )

    const active = new Set(filtered)
    for (const it of index) it.el.classList.toggle('is-hidden', !active.has(it))

    const qs = buildQuery()
    syncLinks(qs) // must precede resetGrid — the grid teasers read it.href

    resetGrid()
    applyView()
    // measureChrome() // EXPERIMENTAL — DISABLED (typewriter scroll-snap): re-read chrome height after the view class flips
    writeUrl(qs)
    startIdleRender()
  }

  // User-driven filter CHANGES return the page to the top: the old scroll
  // position refers to results that no longer exist. Initial load keeps
  // calling apply() directly, so restoreScroll() stays intact. (Opening the
  // panel scrolls separately, and only as far as needed — see the
  // data-toggle-trigger listener.)
  const returnToTop = () => {
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Scroll home FIRST, swap the list on arrival — the other order visibly
  // "hops": the shrinking result set clamps the scroll position in a hard jump
  // before the smooth scroll even starts. A 50ms interval (not rAF, which
  // background tabs freeze) watches for arrival, with a timeout so an
  // interrupted scroll still applies; the 800ms cap comfortably covers the
  // smooth scroll. One watcher is enough for rapid changes — apply() reads the
  // live form state, so the pending swap always shows the newest filter.
  let pendingApply = null
  const applyAndReturnTop = () => {
    if (window.scrollY <= 1) {
      apply()
      return
    }
    returnToTop()
    if (pendingApply) return
    const started = Date.now()
    pendingApply = setInterval(() => {
      if (window.scrollY <= 1 || Date.now() - started > 800) {
        clearInterval(pendingApply)
        pendingApply = null
        apply()
      }
    }, 50)
  }
  const debouncedApply = debounce(applyAndReturnTop, 175)

  // Infinite scroll for the image grid. A hidden sentinel (list view) never
  // intersects, so this is naturally inert there.
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0].isIntersecting) return
      if (state.view !== 'bilder' || rendered >= filtered.length) return
      renderNextBatch()
      // Re-arm in case one batch didn't push the sentinel past the root margin.
      if (rendered < filtered.length) {
        requestAnimationFrame(() => {
          observer.unobserve(sentinel)
          observer.observe(sentinel)
        })
      }
    },
    { rootMargin: '400px' },
  )

  // Idle pre-render: after window.load, mount the remaining grid batches one
  // per idle slot, so their images exist in the DOM and lazysizes
  // (preloadAfterLoad, see lazysizes-config.js) warms them before the user
  // scrolls. The token aborts a stale loop when a filter change replaces the
  // grid mid-run. List view leaves the loop running harmlessly: the grid is
  // display:none there and lazysizes skips hidden images until the view flips.
  let idleToken = 0
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300))
  const idleRenderRest = () => {
    const token = ++idleToken
    const step = () => {
      if (token !== idleToken || !fastConnection()) return
      if (rendered >= filtered.length) return
      renderNextBatch()
      idle(step)
    }
    idle(step)
  }
  const startIdleRender = () => {
    const whenLoaded = () => {
      if (document.readyState === 'complete') idleRenderRest()
      else window.addEventListener('load', idleRenderRest, { once: true })
    }
    // Inside a Chromium prerender (library/preload-projects.php,
    // library/speculative-loading.php) hold the idle pre-render back — it
    // would download every remaining teaser for a page the user may never
    // activate. prerenderingchange fires once at activation (still behind the
    // load gate: activating an unfinished prerender must not mount the full
    // grid during initial load); a stale listener from a superseded apply()
    // is defused by idleToken like any other loop.
    if (document.prerendering) {
      document.addEventListener('prerenderingchange', whenLoaded, { once: true })
      return
    }
    whenLoaded()
  }

  // Restore the form controls from the URL before the first apply.
  const restoreFromUrl = () => {
    const p = new URLSearchParams(location.search)

    const view = p.get('anzeige') === 'liste' ? 'liste' : 'bilder'
    const radio = form.querySelector(`input[name="anzeige"][value="${view}"]`)
    if (radio) radio.checked = true

    const search = form.querySelector('input[name="search"]')
    if (search) search.value = p.get('search') || ''

    for (const tax of TAXONOMIES) {
      const slugs = (p.get(tax) || '').split(',').filter(Boolean)
      for (const slug of slugs) {
        const box = form.querySelector(`input[name="${tax}[]"][value="${CSS.escape(slug)}"]`)
        if (box) box.checked = true
      }
    }
  }

  // "Filter zurücksetzen" in the empty state: clear the search + every tag, but keep
  // the current view (bilder/liste) — resetting the view too would feel like a jump.
  const resetFilters = () => {
    const search = form.querySelector('input[name="search"]')
    if (search) search.value = ''
    for (const tax of TAXONOMIES) {
      form.querySelectorAll(`input[name="${tax}[]"]`).forEach((box) => {
        box.checked = false
      })
    }
    applyAndReturnTop()
  }

  // Preserve the list scroll position across a round-trip to a project detail.
  // The detail page's "schliessen" link returns to this list URL with the same
  // filter (see projekt.php), so only scrollY needs restoring. Keyed by the
  // normalised list URL, so a fresh visit or a different filter never jumps.
  const SCROLL_KEY = 'projectsListScroll'
  const navKey = () =>
    location.pathname +
    '?' +
    [...new URLSearchParams(location.search).entries()].sort().map(([k, v]) => `${k}=${v}`).join('&')

  window.addEventListener('pagehide', () => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ key: navKey(), y: window.scrollY }))
    } catch {
      /* private mode / quota — scroll just isn't restored */
    }
  })

  // Only a return FROM a project detail should restore — reaching this list
  // fresh (menu, bookmark) must start at the top. The project CPT lives under
  // /projekt/ (note the list is /projekte/, which does not contain "/projekt/").
  const cameFromProject = () => {
    try {
      return new URL(document.referrer).pathname.includes('/projekt/')
    } catch {
      return false
    }
  }

  const restoreScroll = () => {
    let saved
    try {
      saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null')
    } catch {
      saved = null
    }
    if (!saved) return
    sessionStorage.removeItem(SCROLL_KEY)
    if (saved.key !== navKey() || saved.y <= 0 || !cameFromProject()) return
    // The image grid renders lazily; render enough batches to reach the saved
    // offset before scrolling (the text list already holds every row).
    let guard = 0
    while (
      state.view === 'bilder' &&
      rendered < filtered.length &&
      document.documentElement.scrollHeight < saved.y + window.innerHeight &&
      guard++ < 500
    ) {
      renderNextBatch()
    }
    window.scrollTo(0, saved.y)
  }

  // Enter in the search field: the results are already live (input handler), so
  // a submit just closes the panel — same as tapping "Filter" again — and blurs
  // the field to drop the mobile keyboard.
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (container.classList.contains('is-active')) {
      container.querySelector('[data-toggle-trigger]')?.click()
    }
    form.querySelector('input[name="search"]')?.blur()
  })
  form.addEventListener('change', (e) => {
    // Clicking "Alle" resets its group: it stays checked (never toggles off) and
    // clears every selected tag in that group. syncAllStates in apply() re-affirms it.
    if (e.target.matches?.('[data-filter-all]')) {
      e.target.checked = true
      form.querySelectorAll(`input[name="${e.target.dataset.filterAll}[]"]`).forEach((box) => {
        box.checked = false
      })
    }
    // Bilder/Liste is a pure presentation flip of the same result set — the
    // new view starts at its top IMMEDIATELY, without the animated return the
    // filters use. 'instant' (not plain scrollTo) is load-bearing: the page
    // has CSS scroll-behavior: smooth, which would animate the jump.
    if (e.target.name === 'anzeige') {
      window.scrollTo({ top: 0, behavior: 'instant' })
      apply()
      return
    }
    applyAndReturnTop()
  })
  form.addEventListener('input', (e) => {
    if (e.target.name === 'search') debouncedApply()
  })
  container.querySelector('[data-projects-reset]')?.addEventListener('click', resetFilters)

  // Opening the panel scrolls only when the open filter would not fully fit.
  // The sticky filter cannot extend past the article's end, so near the page
  // bottom the opening panel gets pushed up out of view (scroll anchoring
  // keeps the user glued to the bottom while the panel grows above). The
  // largest scrollY at which the open filter still fits pinned is constant in
  // document coordinates — the panel grows the article by exactly its own
  // height, so the panel term cancels:
  //   maxFit = scrollY + articleBottom(viewport) - pinnedTop - filterHeight
  // and any consistent mid-transition snapshot yields the same value. A normal
  // open (scrollY <= maxFit) is left untouched; near the bottom the page is
  // scrolled up just far enough, which on a short list page lands near the
  // top. Deferred a frame: toggle.js flips .is-active in its own click
  // listener and the attachment order between the modules isn't guaranteed.
  container.querySelector('[data-toggle-trigger]')?.addEventListener('click', () => {
    requestAnimationFrame(() => {
      if (!container.classList.contains('is-active')) return
      const filterEl = container.querySelector('[data-projects-filter]')
      if (!filterEl) return
      const pinnedTop = parseFloat(getComputedStyle(filterEl).top) || 0
      const maxFit =
        window.scrollY + container.getBoundingClientRect().bottom - pinnedTop - filterEl.offsetHeight
      // Clamp after the open transition (150ms) and scroll anchoring settle.
      setTimeout(() => {
        if (container.classList.contains('is-active') && window.scrollY > maxFit) {
          window.scrollTo({ top: Math.max(0, maxFit), behavior: 'smooth' })
        }
      }, 400)
    })
  })

  restoreFromUrl()
  observer.observe(sentinel)
  apply()
  restoreScroll() // after apply(): needs the filtered set + first grid batch in place
})
